import LegalLayout, { Section, Bullets } from "@/components/LegalLayout";
import { LEGAL_VERSIONS, PLACEHOLDERS, PROHIBITED_INFORMATION } from "@/constants/legalContent";

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      intro="This Privacy Policy explains what information FindRxStaff collects, how we use it, what other users can see, and the choices you have. We collect only the information reasonably necessary to run a staffing coordination platform. We may update this policy from time to time and will post the updated version here."
    >
      <p className="text-sm text-slate-500" data-testid="privacy-version">
        Privacy Policy version {LEGAL_VERSIONS.privacy}
      </p>

      <Section heading="1. Information we collect">
        <p>
          <strong>Account information:</strong> name, email address, phone number and account type.
        </p>
        <p>
          <strong>Professional information:</strong> worker role(s), license information, certification or
          registration information, years of experience, skills, pharmacy software experience and languages.
        </p>
        <p>
          <strong>Location information:</strong> home ZIP code, preferred ZIP codes, travel radius and, for
          pharmacies, the pharmacy address. We do not request precise GPS location.
        </p>
        <p>
          <strong>Availability information:</strong> available dates, available times and shift preferences.
        </p>
        <p>
          <strong>Platform activity:</strong> shifts viewed and posted, expressions of interest, staffing
          requests, confirmations, notifications and general account activity.
        </p>
      </Section>

      <Section heading="2. Information we ask you not to submit">
        <p>
          FindRxStaff is not intended for patient information or other protected health information. Please do
          not submit:
        </p>
        <Bullets items={PROHIBITED_INFORMATION} />
        <p>
          No patient information is required anywhere in the application. If prohibited information is
          submitted, we may remove it and may suspend the account that submitted it.
        </p>
      </Section>

      <Section heading="3. How we use information">
        <Bullets
          items={[
            "Create and maintain user profiles",
            "Match workers with shifts and display relevant opportunities",
            "Display relevant worker information to pharmacies",
            "Facilitate connections between pharmacies and workers",
            "Send in-app notifications and shift reminders",
            "Maintain calendars and shift records",
            "Improve platform functionality",
            "Prevent fraud and abuse and maintain security",
            "Provide customer support",
            "Comply with legal obligations",
          ]}
        />
      </Section>

      <Section heading="4. What other users can see">
        <p>Depending on the stage of a connection, a pharmacy may see the following about a worker:</p>
        <Bullets
          items={[
            "Display name (and full name once a shift is confirmed)",
            "Worker role(s)",
            "General location or approximate distance",
            "Availability",
            "Professional experience, skills and software",
            "Credential and verification status",
            "Relevant preferences such as minimum rate",
          ]}
        />
        <p>A worker may see the following about a pharmacy:</p>
        <Bullets
          items={[
            "Pharmacy name and general location",
            "Shift information and requirements",
            "Pharmacy software and work environment",
            "Other information relevant to the opportunity",
          ]}
        />
      </Section>

      <Section heading="5. Contact information privacy">
        <p>
          We do not publicly display a worker's personal phone number or email address, or a pharmacy
          contact's personal phone number or email address. Direct contact details are revealed only through
          the platform's connection and confirmation workflow — in practice, once a shift is confirmed for
          both sides. License numbers and other sensitive identifiers are not shown to other users.
        </p>
      </Section>

      <Section heading="6. Data security">
        <p>We use reasonable technical and organizational measures, including:</p>
        <Bullets
          items={[
            "Encrypted transport (HTTPS) for traffic to the platform",
            "Securely hashed passwords — passwords are never stored in plain text",
            "Protected authentication tokens stored in HTTP-only cookies",
            "Role-based access controls that restrict access to private user data",
            "Server-side input validation and protected API endpoints",
            "Separately protected administrative functionality with an audit trail of key admin actions",
            "Avoiding exposure of sensitive database fields to the frontend",
          ]}
        />
        <p>
          No platform can promise perfect security, so we do not describe FindRxStaff as "100% private",
          "completely secure" or "HIPAA compliant". We describe only the measures we actually take.
        </p>
      </Section>

      <Section heading="7. Data retention">
        <p>
          We may retain information for as long as reasonably necessary to provide the service, maintain
          records, resolve disputes, prevent abuse and comply with legal obligations. You can request account
          deletion at any time from your settings.
        </p>
      </Section>

      <Section heading="8. Account deletion">
        <p>
          Deleting your account removes or anonymizes your profile, availability, preferences and active
          expressions of interest where reasonably possible. Certain historical records — such as past shift
          records, ratings, consent records and security logs — may need to be retained for legitimate
          business, legal, security or compliance reasons, so we do not promise immediate permanent deletion of
          every piece of data.
        </p>
      </Section>

      <Section heading="9. Your choices">
        <Bullets
          items={[
            "Update or correct your profile, availability and preferences at any time",
            "Block another user to stop unwanted matching and contact",
            "Report a user or shift for review",
            "Request account deletion from Settings",
            "Review the consent records associated with your account",
          ]}
        />
      </Section>

      <Section heading="10. Changes and contact">
        <p>
          We may update this Privacy Policy from time to time; the version number and Last Updated date above
          will change accordingly. Questions can be directed to {PLACEHOLDERS.email}. Legal entity:{" "}
          {PLACEHOLDERS.entity}.
        </p>
      </Section>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600" data-testid="privacy-counsel-notice">
        <p className="font-medium text-slate-800">Notice regarding this document</p>
        <p className="mt-1.5">
          This document is provided to describe the platform's current product structure and should be
          reviewed and adapted by qualified legal counsel before public launch. It is not legal advice.
        </p>
      </div>
    </LegalLayout>
  );
}
